import React, { useEffect, useState } from "react";
import { Megaphone, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUSES = ["draft", "active", "paused", "archived"];
const EMPTY = { name: "", description: "", status: "draft" };

export default function Campaigns() {
  const { user } = useAuth();
  const canWrite = !user?.restricted && !!user?.can_mutate;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/campaigns");
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name the campaign");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/campaigns/${editing}`, form);
        setRows((rs) => rs.map((r) => (r.id === editing ? data : r)));
        toast.success("Campaign updated");
      } else {
        const { data } = await api.post("/campaigns", form);
        setRows((rs) => [data, ...rs]);
        toast.success("Campaign created");
      }
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save campaign");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/campaigns/${deleteId}`);
      setRows((rs) => rs.filter((r) => r.id !== deleteId));
      toast.success("Campaign deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't delete");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div data-testid="campaigns-page">
      <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Workspace</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Campaigns</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Name a body of work, then ask EMIE to create, update, or retire it — if you have change permission.
          </p>
        </div>
        <Button
          onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }}
          disabled={!canWrite}
          className="gap-2 font-semibold"
          data-testid="add-campaign-button"
        >
          <Plus className="h-4 w-4" /> New campaign
        </Button>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border/60 p-10 text-center" data-testid="campaigns-empty">
          <Megaphone className="h-8 w-8 text-primary" />
          <p className="mt-4 font-display text-lg font-bold">No campaigns yet</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Ask EMIE: “Create a campaign called Q3 Salesforce limits.” Or add one here.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="campaigns-list">
          {rows.map((c) => (
            <div key={c.id} className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-display font-bold">{c.name}</p>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{c.status}</p>
                {c.description && <p className="mt-1 text-sm text-muted-foreground">{c.description}</p>}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={!canWrite} onClick={() => { setEditing(c.id); setForm({ name: c.name, description: c.description || "", status: c.status }); setOpen(true); }}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" size="sm" disabled={!canWrite} onClick={() => setDeleteId(c.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit campaign" : "New campaign"}</DialogTitle>
            <DialogDescription>This lives in your workspace. Billing and keys stay out of reach.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="campaign-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} data-testid="campaign-save">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this campaign?</AlertDialogTitle>
            <AlertDialogDescription>Jobs and rules attached to it are not deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
